import { ImagesEntity } from '@bitecritic-api/src/images/images.zod';
import { ImageUploadType } from '@bitecritic-api/src/images/images.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import imagesService from '~/services/imagesService/images.service';

export const useImageUpload = (existingImages: ImagesEntity[] = []) => {
  const queryClient = useQueryClient();

  // Initialize query cache with existing images
  queryClient.setQueryData<Array<ImagesEntity>>(
    ['uploadedImages'],
    existingImages,
    { updatedAt: Date.now() }
  );

  const uploadMutation = useMutation({
    mutationFn: async ({ file, type }: { file: File; type: ImageUploadType }) => {
      const formData = new FormData();
      formData.append('file', file);
      return imagesService.uploadImage(formData, type);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Array<ImagesEntity>>(
        ['uploadedImages'],
        (old = []) => [...old, data]
      );
    },
    onError: (error) => {
      console.error('Error uploading image:', error);
      throw error;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (imageId: string) => imagesService.deleteImage(imageId),
    onSuccess: (_, imageId) => {
      queryClient.setQueryData<Array<{ id: string; url: string }>>(
        ['uploadedImages'],
        (old = []) => old.filter((img) => img.id !== imageId)
      );
    },
    onError: (error) => {
      console.error('Error deleting image:', error);
      throw error;
    }
  });

  const uploadImage = async (file: File, type: ImageUploadType) => {
    return uploadMutation.mutateAsync({ file, type });
  };

  const deleteImage = async (imageId: string) => {
    return deleteMutation.mutateAsync(imageId);
  };

  const uploadedImages = queryClient.getQueryData<Array<ImagesEntity>>(
    ['uploadedImages']
  ) || [];

  return {
    uploadedImages,
    uploadImage,
    deleteImage,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    uploadError: uploadMutation.error,
    deleteError: deleteMutation.error,
  };
};